"use client";

import React from 'react'
import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'
import { motion } from 'framer-motion'
import { Shield, Zap, Users, ArrowRight } from "lucide-react";

import Link from 'next/link'
import Image from 'next/image'

import CommonHero from '@/components/CommonHero'
import CommonCTA from '@/components/CommonCTA'

export default function About() {
  const values = [
    {
      icon: <Shield size={24} />,
      title: "Integrity-First Guidance",
      description: "We provide honest, transparent advice and always recommend solutions that best serve our clients’ long-term interests."
    },
    {
      icon: <Zap size={24} />,
      title: "Efficiency & Speed",
      description: "We streamline processes using modern systems and experience-driven workflows to ensure fast and reliable service delivery."
    },
    {
      icon: <Users size={24} />,
      title: "Long-Term Partnership",
      description: "We believe in building lasting relationships and supporting our clients beyond company formation, acting as a trusted business partner at every stage."
    }
  ]

  return (
    <main className="min-h-screen bg-[#FAFAFA] overflow-x-hidden ">
      <Navbar />

      <CommonHero
        badge="About Us"
        title="AWS Businessmen Services"
        highlight="Business Setup • Visa Services • Immigration • Accounting & Tax"
        description="Your trusted UAE partner for company formation, residency solutions, and corporate compliance."
      >
        <Link
          href="/contact"
          className="w-full sm:w-auto px-8 py-4 rounded-xl bg-[#B8893A] text-white font-bold shadow-lg hover:bg-[#A67B34] transition-all hover:scale-[1.03] flex items-center justify-center gap-2"
        >
          Book Free Consultation
        </Link>
        <Link
          href="/calculator"
          className="w-full sm:w-auto px-8 py-4 rounded-xl border-2 border-[#B8893A] text-[#B8893A] font-bold hover:bg-[#B8893A]/5 transition-all flex items-center justify-center gap-2"
        >
          Get Instant Quote
        </Link>
      </CommonHero>

      {/* Intro Section */}
      <section className="py-16 sm:py-24 bg-white px-4 sm:px-6">
        <div className="main-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* LEFT — visual accent */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* big gold background block */}
              <div className="absolute -top-6 -left-6 w-2/3 h-2/3 bg-[#B8893A]/10 rounded-3xl -z-10" />
              <div className="absolute -bottom-6 -right-6 w-1/2 h-1/2 bg-[#B8893A]/5 rounded-3xl -z-10" />

              <div className="bg-[#FAFAFA] border border-gray-100 rounded-3xl p-8 sm:p-10 shadow-lg space-y-6">

                {/* stat pills */}
                <div className="flex flex-wrap gap-3">
                  {["Company Formation", "Visa Services", "Immigration", "Accounting & Tax"].map((tag) => (
                    <span key={tag} className="px-4 py-1.5 rounded-full bg-[#B8893A]/10 text-[#B8893A] text-sm font-semibold">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="h-px bg-gray-100" />

                {/* quote block */}
                <blockquote className="relative pl-5 border-l-4 border-[#B8893A] italic text-gray-600 text-base sm:text-lg leading-relaxed">
                  "Our approach is built on efficiency, accuracy, and full regulatory compliance, ensuring a smooth and reliable experience from initial setup through to long-term growth and operations."
                </blockquote>

                <div className="h-px bg-gray-100" />

                {/* quick stats row */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  {[
                    { value: "UAE", label: "Based" },
                    { value: "100%", label: "Compliant" },
                    { value: "End-to-End", label: "Solutions" },
                  ].map((s) => (
                    <div key={s.label}>
                      <p className="text-xl sm:text-2xl font-black text-[#B8893A]">{s.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>

              </div>
            </motion.div>

            {/* RIGHT — text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#B8893A] font-semibold">
                Who We Are
              </p>

              <h1
                className="font-bold text-black leading-tight"
                style={{
                  fontSize: "clamp(28px, 5vw, 48px)",
                  fontWeight: "700",
                  lineHeight: "1.3"
                }}
              >
                About <span className="text-[#B8893A]">AWS Businessmen Services</span>
              </h1>

              <p
                className="text-gray-600"
                style={{
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  lineHeight: "1.6"
                }}
              >
                At AWS Businessmen Services, we position ourselves as a reliable partner in your business journey. Our approach focuses on delivering clear, professional, and effective solutions — moving beyond standard services to support long-term business success in the UAE.
              </p>

              <p
                className="text-gray-500"
                style={{
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  lineHeight: "1.6"
                }}
              >
                We are a professional consultancy specializing in business setup and market entry in the United Arab Emirates. We support entrepreneurs, investors, and growing businesses with complete solutions, including company formation, visa services, immigration services, and accounting and tax support.
              </p>

              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#B8893A] text-white font-bold shadow hover:bg-[#A67B34] transition-all hover:scale-[1.03]"
              >
                Book Free Consultation <ArrowRight size={18} />
              </Link>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA] px-4 sm:px-6">
        <div className="main-container">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT IMAGE */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative w-full h-[300px] sm:h-[450px] lg:h-[550px] rounded-3xl overflow-hidden shadow-2xl">
                <Image
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=1200"
                  alt="Our Story"
                  fill
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
            </motion.div>

            {/* RIGHT CONTENT */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="px-2"
            >
              <h1
                className="font-bold text-black mb-6 leading-tight"
                style={{
                  fontSize: "clamp(28px, 5vw, 48px)",
                  fontWeight: "700",
                  lineHeight: "1.3"
                }}
              >
                Our <span className="text-[#B8893A]">Story</span>
              </h1>
              <div
                className="space-y-6 text-gray-600"
                style={{
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  lineHeight: "1.6"
                }}
              >
                <p>
                  AWS Businessmen Services was founded with a simple idea to make starting and managing a business in the UAE easier, clearer, and more reliable. We saw that many entrepreneurs needed more than just documentation; they needed a trusted partner who could guide them through every step with confidence.
                </p>
                <p>
                  From that vision, we began as a focused consultancy and have grown into a complete business solutions provider. Today, we offer support across business setup, visa and immigration services, and corporate solutions, helping clients turn ideas into real, functioning businesses.
                </p>
                <p>
                  Our journey has grown alongside the UAE’s own development. As the country evolved into a global business hub, we continuously improved our services, embraced modern processes, and adapted to changing regulations. This allows us to provide faster, more efficient, and more dependable support to every client we serve.
                </p>
                <p className="font-semibold text-black">
                  At AWS Businessmen Services, we remain committed to making business setup and growth in the UAE a smooth and positive experience for everyone we work with.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 sm:py-24 bg-white px-4 sm:px-6">
        <div className="main-container">
          <div className="text-center mb-16">
            <h1
              className="font-bold text-black mb-6"
              style={{
                fontSize: "clamp(28px, 5vw, 48px)",
                fontWeight: "700",
                lineHeight: "1.3"
              }}
            >
              Our Mission & <span className="text-[#B8893A]">Values</span>
            </h1>
            <p
              className="text-gray-600 max-w-3xl mx-auto"
              style={{
                fontSize: "clamp(14px, 1.2vw, 16px)",
                lineHeight: "1.6"
              }}
            >
              Our mission is to make business setup in the UAE simple, efficient, and accessible. We aim to remove complexity from licensing, visa, and compliance processes so our clients can focus on building and growing their businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <div key={i} className="p-8 rounded-2xl bg-[#FAFAFA] border border-gray-100 hover:shadow-xl transition-all group">
                <div className="w-14 h-14 rounded-xl bg-[#B8893A]/10 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all">
                  {v.icon}
                </div>
                <h3
                  className="font-bold text-black mb-4"
                  style={{
                    fontSize: "clamp(18px, 2vw, 20px)",
                    fontWeight: "600",
                    lineHeight: "1.3"
                  }}
                >
                  {v.title}
                </h3>
                <p
                  className="text-gray-600"
                  style={{
                    fontSize: "clamp(14px, 1.2vw, 16px)",
                    lineHeight: "1.6"
                  }}
                >
                  {v.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Track Record */}
      <section className="py-16 sm:py-20 bg-gray-50 border-y border-gray-100 px-4 sm:px-6">
        <div className="main-container text-center">
          <h1
            className="font-bold text-black mb-6"
            style={{
              fontSize: "clamp(28px, 5vw, 48px)",
              fontWeight: "700",
              lineHeight: "1.3"
            }}
          >
            Our Track Record
          </h1>
          <p
            className="text-gray-600 max-w-4xl mx-auto"
            style={{
              fontSize: "clamp(14px, 1.2vw, 16px)",
              lineHeight: "1.6"
            }}
          >
            AWS Businessmen Services is a growing consultancy in the UAE, supporting entrepreneurs and businesses with company formation, visa services, and corporate solutions. We focus on delivering reliable support and building long-term client success as we continue to expand.
          </p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-24 bg-white px-4 sm:px-6">
        <div className="main-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1
                className="font-bold text-black mb-8 leading-tight"
                style={{
                  fontSize: "clamp(28px, 5vw, 48px)",
                  fontWeight: "700",
                  lineHeight: "1.3"
                }}
              >
                Why Choose <span className="text-[#B8893A]">Us</span>
              </h1>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#B8893A] text-white flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <p
                    className="text-gray-600"
                    style={{
                      fontSize: "clamp(14px, 1.2vw, 16px)",
                      lineHeight: "1.6"
                    }}
                  >
                    We combine deep knowledge of UAE business regulations with a practical, results-driven approach to deliver fast and reliable company formation, visa processing, and corporate services.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#B8893A] text-white flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <p
                    className="text-gray-600"
                    style={{
                      fontSize: "clamp(14px, 1.2vw, 16px)",
                      lineHeight: "1.6"
                    }}
                  >
                    Our team ensures every step is handled with accuracy, compliance, and efficiency, reducing delays and removing complexity from the process.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#B8893A] text-white flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <p
                    className="text-gray-600"
                    style={{
                      fontSize: "clamp(14px, 1.2vw, 16px)",
                      lineHeight: "1.6"
                    }}
                  >
                    What truly sets us apart is our commitment to long-term support, we don’t just help you start your business, we help you build and grow it with confidence in the UAE.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative h-[400px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl">
              <Image
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=1200"
                alt="Why Choose Us"
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA] px-4 sm:px-6">
        <div className="main-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* LEFT — text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#B8893A] font-semibold">
                The People Behind AWS
              </p>

              <h1
                className="font-bold text-black leading-tight"
                style={{
                  fontSize: "clamp(28px, 5vw, 48px)",
                  fontWeight: "700",
                  lineHeight: "1.3"
                }}
              >
                Our <span className="text-[#B8893A]">Team</span>
              </h1>

              <p
                className="text-gray-600"
                style={{
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  lineHeight: "1.6"
                }}
              >
                The strength of AWS Businessmen Services comes from our experienced and dedicated team. We bring together professionals with strong expertise in business setup, visa services, legal processes, and corporate support across the UAE.
              </p>

              <blockquote
                className="pl-5 border-l-4 border-[#B8893A] italic text-gray-500"
                style={{
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  lineHeight: "1.6"
                }}
              >
                "With in-depth knowledge of UAE regulations and procedures, our team ensures every client receives clear guidance, efficient execution, and personalized support at every stage of their business journey."
              </blockquote>

              <p
                className="text-black font-semibold"
                style={{
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  lineHeight: "1.6"
                }}
              >
                At AWS Businessmen Services, we work as one team to help you build, manage, and grow your business with confidence in the UAE.
              </p>
            </motion.div>

            {/* RIGHT — expertise cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { title: "Business Setup", desc: "Expert guidance on company formation across mainland, free zone, and offshore." },
                { title: "Visa Services", desc: "End-to-end visa processing for investors, employees, and families." },
                { title: "Legal Processes", desc: "Full regulatory compliance and documentation handled with precision." },
                { title: "Corporate Support", desc: "Accounting, tax, and ongoing corporate services for long-term growth." },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition-all group"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#B8893A]/10 flex items-center justify-center mb-4 group-hover:bg-[#B8893A] transition-all">
                    <div className="w-3 h-3 rounded-full bg-[#B8893A] group-hover:bg-white transition-all" />
                  </div>
                  <h3 className="font-bold text-black text-sm sm:text-base mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </motion.div>

          </div>
        </div>
      </section>

      <CommonCTA
        title="Let’s Build Your Business Together"
        description="Whether you are starting a new company or expanding into the UAE, AWS Businessmen Services is here to guide you at every step. We simplify the process so you can focus on what matters most—growing your business."
      />

      <Footer />
    </main>
  )
}
